import { showNudgeDownloadApp } from "@/components/nudge-download-app-utils";
import { useAutoDeleteValues } from "@/hooks/use-auto-delete-values";
import { useDesktop } from "@/hooks/use-desktop";
import { useOrganization } from "@/queries/organization-queries";
import { useGetPreferenceByKey, type PlainUserPreference } from "@/queries/user-preferences-queries";
import { useAuthenticatedUser, type PlainUser } from "@/queries/user-queries";
import { shouldShowUpdateNudge } from "@/version-tracker";
import { OrganizationTier } from "gitpod-next-api/gitpod/v1/organization_pb";

export const PRIVACY_POLICY_LAST_UPDATED_DATE = new Date("2026-04-13T00:00:00Z");

export enum NudgeKeys {
    "PRIVACY_POLICY_UPDATED" = "PRIVACY_POLICY_UPDATED",

    "AUTO_DELETE_POLICY" = "AUTO_DELETE_POLICY",
    "VERSION_UPDATE" = "VERSION_UPDATE",
    "AUTOMATIONS_LAUNCH" = "AUTOMATIONS_LAUNCH",
    // Intentionally differs from the old key ("SHOW_NUDGE_DOWNLOAD_APP") so users
    // who dismissed the previous popover-style nudge see the new bottom-left placement.
    "DOWNLOAD_APP" = "DOWNLOAD_APP",
}

type Nudge =
    | { key: NudgeKeys.PRIVACY_POLICY_UPDATED }
    | { key: NudgeKeys.AUTO_DELETE_POLICY }
    | { key: NudgeKeys.VERSION_UPDATE }
    | { key: NudgeKeys.AUTOMATIONS_LAUNCH }
    | { key: NudgeKeys.DOWNLOAD_APP };

/**
 * Computes the active nudge (if any).
 * This ensures we only ever render one nudge at a time.
 */
export function useNudges(): { active?: Nudge; isPending: boolean; error: Error | null } {
    const { data: user, isPending: isPendingUser, error: errorUser } = useAuthenticatedUser();
    const { data: organization, isPending: isPendingOrg, error: errorOrg } = useOrganization();
    const { isDesktop } = useDesktop();
    const {
        data: automationsLaunchPref,
        isPending: isPendingAutomationsLaunch,
        error: errorAutomationsLaunch,
    } = useGetPreferenceByKey(NudgeKeys.AUTOMATIONS_LAUNCH);
    const {
        data: privacyPolicyPref,
        isPending: isPendingPrivacyPolicy,
        error: errorPrivacyPolicy,
    } = useGetPreferenceByKey(NudgeKeys.PRIVACY_POLICY_UPDATED);
    const {
        data: downloadAppPref,
        isPending: isPendingDownloadApp,
        error: errorDownloadApp,
    } = useGetPreferenceByKey(NudgeKeys.DOWNLOAD_APP);

    const { currentAutoDeletePolicyValue, previousAutoDeletePolicyValue } = useAutoDeleteValues();

    const isPendingPreferences = isPendingAutomationsLaunch || isPendingPrivacyPolicy || isPendingDownloadApp;
    const errorPreferences = errorAutomationsLaunch || errorPrivacyPolicy || errorDownloadApp;

    if (isPendingUser || isPendingOrg || isPendingPreferences) {
        return { isPending: true, error: null };
    }

    if (errorUser || errorOrg || errorPreferences) {
        return { isPending: false, error: errorUser || errorOrg || errorPreferences };
    }

    if (!user) {
        return { isPending: isPendingUser, error: errorUser };
    }

    // Automations launch nudge — show to Core and FREE_ONA users
    const isCore = organization?.tier === OrganizationTier.CORE;
    const isFreeOna = organization?.tier === OrganizationTier.FREE_ONA;
    if ((isCore || isFreeOna) && !hasDismissedNudge(automationsLaunchPref)) {
        return {
            active: { key: NudgeKeys.AUTOMATIONS_LAUNCH },
            isPending: false,
            error: null,
        };
    }

    // Enterprise orgs don't get privacy policy nudges
    const isEnterprise = organization?.tier === OrganizationTier.ENTERPRISE;
    if (!isEnterprise && !hasAcceptedCurrentPrivacyPolicy({ user, preference: privacyPolicyPref })) {
        return {
            active: { key: NudgeKeys.PRIVACY_POLICY_UPDATED },
            isPending: isPendingPreferences,
            error: errorPreferences,
        };
    }

    // Check for auto-delete policy changes
    // Show nudge only if there was a previous value AND it differs from current value
    // For new users (no previous value), don't show the nudge
    if (previousAutoDeletePolicyValue !== undefined && previousAutoDeletePolicyValue !== currentAutoDeletePolicyValue) {
        return {
            active: { key: NudgeKeys.AUTO_DELETE_POLICY },
            isPending: isPendingPreferences,
            error: errorPreferences,
        };
    }

    // Download app nudge — macOS web users on Free/Core tiers
    const tierEnabled = isCore || isFreeOna;
    if (showNudgeDownloadApp(isDesktop, tierEnabled, isPendingOrg) && !hasDismissedNudge(downloadAppPref)) {
        return {
            active: { key: NudgeKeys.DOWNLOAD_APP },
            isPending: false,
            error: null,
        };
    }

    // Version update nudge (lowest priority) — uses client-side state from version tracker
    if (shouldShowUpdateNudge()) {
        return {
            active: { key: NudgeKeys.VERSION_UPDATE },
            isPending: false,
            error: null,
        };
    }

    return { isPending: isPendingPreferences, error: errorPreferences };
}

/**
 * Returns true when the active nudge is VERSION_UPDATE.
 * Used by the sidebar update chip and dot indicators.
 */
export function useIsVersionUpdateAvailable(): boolean {
    const { active, isPending, error } = useNudges();
    if (isPending || error) {
        return false;
    }
    return active?.key === NudgeKeys.VERSION_UPDATE;
}

function hasDismissedNudge(preference: PlainUserPreference | null | undefined): boolean {
    if (!preference?.value || preference.value === "false") {
        return false;
    }

    return !!preference?.value;
}

function hasAcceptedCurrentPrivacyPolicy(options: {
    user: PlainUser;
    preference: PlainUserPreference | null | undefined;
}): boolean {
    const { user, preference } = options;

    // If the user was created after the last privacy policy update, we don't need to show the nudge
    if (user.createdAt?.seconds && user.createdAt?.seconds > PRIVACY_POLICY_LAST_UPDATED_DATE.getTime() / 1000) {
        return true;
    }

    // If the user has already accepted the current privacy policy, we don't need to show the nudge
    if (preference?.value) {
        const acceptedAt = new Date(preference?.value);
        if (acceptedAt > PRIVACY_POLICY_LAST_UPDATED_DATE) {
            return true;
        }
    }

    return false;
}
